import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, UserPlus2, Key } from "lucide-react";
import UserFormModal from "@/components/UserFormModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import SetPasswordModal from "@/components/SetPasswordModal";
import { useAuth } from "@/context/AuthContext";

export default function UsersPage() {
  const { roleName, isAdmin } = useAuth();
  const isDesignHead = roleName === "Design Head" && !isAdmin;
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ open: false, mode: "add", user: null });
  const [confirm, setConfirm] = useState({ open: false, user: null });
  const [pwdModal, setPwdModal] = useState({ open: false, user: null });

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const roleById = useMemo(() => Object.fromEntries(roles.map(r => [r.id, r])), [roles]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  // Roles the current caller may create/edit. Design Head is restricted to Design Manager
  // + Interior Designer per backend policy (see _authorize_user_write in server.py).
  const editableRoles = useMemo(() => {
    if (!isDesignHead) return roles;
    return roles.filter(r => r.name === "Design Manager" || r.name === "Interior Designer");
  }, [roles, isDesignHead]);

  const filtered = useMemo(() => {
    // Design Head sees only Design Manager + Interior Designer users.
    let base = users;
    if (isDesignHead) {
      const allowedRoleIds = new Set(editableRoles.map(r => r.id));
      base = users.filter(u => allowedRoleIds.has(u.role_id));
    }
    if (!q.trim()) return base;
    const s = q.toLowerCase();
    return base.filter(u =>
      u.name.toLowerCase().includes(s) ||
      u.email.toLowerCase().includes(s) ||
      (roleById[u.role_id]?.name || "").toLowerCase().includes(s) ||
      (u.employee_id || "").toLowerCase().includes(s)
    );
  }, [q, users, roleById, isDesignHead, editableRoles]);

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "deactive" : "active";
    try {
      await api.patch(`/users/${u.id}/status`, { status: next });
      toast.success(`${u.name} → ${next}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    const u = confirm.user;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} removed. Reports reassigned to Director.`);
      setConfirm({ open: false, user: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const managerCount = users.filter(u => roleById[u.role_id]?.type === "manager").length;
  const execCount = users.filter(u => roleById[u.role_id]?.type === "executive").length;
  const activeCount = users.filter(u => u.status === "active").length;

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Directory</p>
          <h1 className="font-display text-3xl text-white mt-1">User Management</h1>
        </div>
        <button
          className="iz-btn-primary flex items-center gap-2"
          onClick={() => setModal({ open: true, mode: "add", user: null })}
          data-testid="add-user-button"
        >
          <UserPlus2 className="w-4 h-4" strokeWidth={2}/> Add User
        </button>
      </div>

      <div className="px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Users", value: users.length },
            { label: "Managers", value: managerCount },
            { label: "Executives", value: execCount },
            { label: "Active", value: activeCount, gold: true },
          ].map((s) => (
            <div key={s.label} className="iz-card p-5">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">{s.label}</p>
              <p className={`font-display text-3xl mt-2 ${s.gold ? "text-[#d4af37]" : "text-white"}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" strokeWidth={1.5}/>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search users, roles, emails…"
              className="iz-input pl-10"
              data-testid="user-search-input"
            />
          </div>
          <p className="text-xs font-mono uppercase tracking-widest text-gray-500">
            {filtered.length} / {users.length}
          </p>
        </div>

        {/* Table */}
        <div className="iz-card overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#262626] bg-[#0f0f0f]">
                {["Name", "Employee ID", "Role", "Contact", "Reports To", "Status", "Actions"].map((h) => (
                  <th key={h} className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 py-4 px-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-10 text-center text-gray-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-gray-500">No users found</td></tr>
              ) : filtered.map((u) => {
                const role = roleById[u.role_id];
                const manager = u.reports_to_user_id ? userById[u.reports_to_user_id] : null;
                return (
                  <tr key={u.id} className="border-b border-[#1a1a1a] hover:bg-[#0e0e0e] transition-colors group" data-testid={`user-row-${u.id}`}>
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#1a1a1a] border border-[#262626] flex items-center justify-center text-xs font-semibold text-[#d4af37]">
                          {u.name.split(" ").map(n => n[0]).slice(0,2).join("")}
                        </div>
                        <div>
                          <p className="text-sm text-white">{u.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-5" data-testid={`user-empid-${u.id}`}>
                      {u.employee_id
                        ? <span className="text-xs font-mono text-gray-300">{u.employee_id}</span>
                        : <span className="text-[10px] font-mono italic text-gray-600">Not set</span>}
                    </td>
                    <td className="py-4 px-5">
                      <span className="inline-flex items-center gap-2 text-xs">
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          role?.type === "director" ? "bg-[#d4af37]" : role?.type === "manager" ? "bg-white" : "bg-gray-500"
                        }`} />
                        <span className="text-gray-300">{role?.name || "—"}</span>
                      </span>
                    </td>
                    <td className="py-4 px-5 text-xs text-gray-400 font-mono">{u.phone}</td>
                    <td className="py-4 px-5 text-sm text-gray-400">
                      {manager ? manager.name : role?.type === "director" ? <em className="text-[#d4af37]">Self</em> : "—"}
                    </td>
                    <td className="py-4 px-5">
                      <button
                        onClick={() => toggleStatus(u)}
                        className="flex items-center gap-3"
                        data-testid={`toggle-status-${u.id}`}
                      >
                        <span className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${u.status === "active" ? "bg-[#d4af37]" : "bg-[#262626]"}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-black transition-transform ${u.status === "active" ? "translate-x-4" : "translate-x-0.5"}`} />
                        </span>
                        <span className={`text-[10px] font-mono uppercase tracking-wider ${u.status === "active" ? "text-[#d4af37]" : "text-gray-500"}`}>
                          {u.status}
                        </span>
                      </button>
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPwdModal({ open: true, user: u })}
                          className="p-2 rounded hover:bg-[#1c1c1c] text-gray-400 hover:text-[#d4af37] transition-colors"
                          data-testid={`set-password-${u.id}`}
                          title={u.has_password ? "Reset password" : "Set login password"}
                        >
                          <Key className="w-4 h-4" strokeWidth={1.5}/>
                        </button>
                        <button
                          onClick={() => setModal({ open: true, mode: "edit", user: u })}
                          className="p-2 rounded hover:bg-[#1c1c1c] text-gray-400 hover:text-[#d4af37] transition-colors"
                          data-testid={`edit-user-${u.id}`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" strokeWidth={1.5}/>
                        </button>
                        <button
                          onClick={() => setConfirm({ open: true, user: u })}
                          className="p-2 rounded hover:bg-red-500/10 text-gray-400 hover:text-red-400 transition-colors"
                          data-testid={`delete-user-${u.id}`}
                          title="Delete"
                          disabled={role?.type === "director"}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <UserFormModal
        open={modal.open}
        mode={modal.mode}
        user={modal.user}
        roles={editableRoles}
        onClose={() => setModal({ open: false, mode: "add", user: null })}
        onSaved={() => { setModal({ open: false, mode: "add", user: null }); load(); }}
      />

      <ConfirmDialog
        open={confirm.open}
        title="Delete user?"
        description={confirm.user ? `Permanently remove ${confirm.user.name}. Their direct reports will be auto-reassigned to the Director.` : ""}
        confirmLabel="Delete"
        onCancel={() => setConfirm({ open: false, user: null })}
        onConfirm={doDelete}
      />

      <SetPasswordModal
        open={pwdModal.open}
        user={pwdModal.user}
        onClose={() => setPwdModal({ open: false, user: null })}
        onSaved={() => { setPwdModal({ open: false, user: null }); load(); }}
      />
    </div>
  );
}
